import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Sparkles, Loader2, CheckCircle2, BarChart3, FileText, Plus, ChevronRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${API_BASE_URL}/api`;

const TABS = [
  { id: 'create', icon: Plus, label: 'Créer une tâche' },
  { id: 'analyze', icon: BarChart3, label: 'Analyser le board' },
  { id: 'notes', icon: FileText, label: 'Notes → Tâches' },
];

const PRIORITY_COLORS = {
  Critical: '#333333',
  High: '#401694',
  Medium: '#5559DF',
  Low: '#579BFC',
};

// Nicely renders AI text response with basic line structure
const AIResponse = ({ text }) => {
  if (!text) return null;
  return (
    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Réponse IA
      </p>
      <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
        {text}
      </div>
    </div>
  );
};

// ─── Tab: Create Task ────────────────────────────────────────────────────────
const CreateTaskTab = ({ board, workspaceId, onTaskCreated }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCreate = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/ai/create-task`,
        { board_id: board._id, workspace_id: workspaceId, text: text.trim() },
        { withCredentials: true }
      );
      setResult(res.data);
      toast({ title: `Tâche "${res.data.task?.task}" créée avec l'IA !` });
      onTaskCreated();
      setText('');
    } catch (err) {
      toast({
        title: 'Erreur IA',
        description: err.response?.data?.detail || 'Impossible de créer la tâche',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Décrivez une tâche en langage naturel. L'IA extraira le titre, l'assignee, la priorité et la date.
      </p>
      <div className="space-y-2">
        <Textarea
          placeholder={`ex: "Préparer la présentation pour Marc lundi avec priorité haute"`}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          className="resize-none text-sm"
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleCreate();
          }}
        />
        <p className="text-xs text-gray-400 dark:text-gray-500">⌘+Entrée pour envoyer</p>
      </div>
      <Button
        onClick={handleCreate}
        disabled={!text.trim() || loading}
        className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Créer avec l'IA
      </Button>

      {result?.task && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{result.task.task}</p>
              <div className="flex gap-2 mt-1 flex-wrap">
                {result.task.priority?.label && (
                  <span className="text-xs px-2 py-0.5 rounded text-white font-medium"
                    style={{ backgroundColor: PRIORITY_COLORS[result.task.priority.label] || '#5559DF' }}>
                    {result.task.priority.label}
                  </span>
                )}
                {result.task.status?.label && (
                  <span className="text-xs px-2 py-0.5 rounded text-white font-medium"
                    style={{ backgroundColor: result.task.status.color || '#CCCCCC' }}>
                    {result.task.status.label}
                  </span>
                )}
                {result.task.date && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{result.task.date}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Analyze Board ──────────────────────────────────────────────────────
const AnalyzeTab = ({ board }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/boards/${board._id}/ai-analyze`, {}, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      toast({
        title: 'Erreur IA',
        description: err.response?.data?.detail || "Impossible d'analyser le board",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Obtenez une analyse IA de la santé du board, des risques et des recommandations stratégiques.
      </p>
      <Button
        onClick={handleAnalyze}
        disabled={loading}
        className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
        Analyser avec l'IA
      </Button>

      {result && (
        <div className="space-y-3">
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{result.statistics?.total_tasks}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Tâches</p>
            </div>
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">{result.statistics?.overdue_tasks}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">En retard</p>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">
                {result.statistics?.status_breakdown?.['Done'] || result.statistics?.status_breakdown?.['Won'] || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Terminées</p>
            </div>
          </div>

          <AIResponse text={result.ai_analysis} />
        </div>
      )}
    </div>
  );
};

// ─── Tab: Parse Notes ────────────────────────────────────────────────────────
const ParseNotesTab = ({ board, workspaceId, onTaskCreated }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsedTasks, setParsedTasks] = useState(null);
  const [creating, setCreating] = useState({});
  const [created, setCreated] = useState({});

  const handleParse = async () => {
    if (!notes.trim()) return;
    setLoading(true);
    setParsedTasks(null);
    setCreated({});
    try {
      const res = await axios.post(
        `${API}/ai/parse-notes`,
        { notes: notes.trim(), board_id: board._id, workspace_id: workspaceId },
        { withCredentials: true }
      );
      setParsedTasks(res.data.parsed_tasks || []);
      toast({ title: `${res.data.total_tasks_found} tâche(s) extraite(s) !` });
    } catch (err) {
      toast({
        title: 'Erreur IA',
        description: err.response?.data?.detail || 'Impossible de parser les notes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOne = async (task, index) => {
    setCreating(prev => ({ ...prev, [index]: true }));
    try {
      await axios.post(
        `${API}/ai/create-task`,
        {
          board_id: board._id,
          workspace_id: workspaceId,
          text: `${task.task}${task.assignee ? ` assigné à ${task.assignee}` : ''}${task.due_date ? ` pour le ${task.due_date}` : ''}${task.priority ? ` priorité ${task.priority}` : ''}`,
        },
        { withCredentials: true }
      );
      setCreated(prev => ({ ...prev, [index]: true }));
      onTaskCreated();
      toast({ title: `"${task.task}" ajoutée au board` });
    } catch {
      toast({ title: 'Erreur lors de la création', variant: 'destructive' });
    } finally {
      setCreating(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleCreateAll = async () => {
    for (let i = 0; i < (parsedTasks || []).length; i++) {
      if (!created[i]) await handleCreateOne(parsedTasks[i], i);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Collez vos notes de réunion et l'IA en extraira les tâches actionnables.
      </p>
      <Textarea
        placeholder="Collez vos notes de réunion ici..."
        value={notes}
        onChange={e => setNotes(e.target.value)}
        rows={6}
        className="resize-none text-sm"
      />
      <Button
        onClick={handleParse}
        disabled={!notes.trim() || loading}
        className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
        Extraire les tâches
      </Button>

      {parsedTasks !== null && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {parsedTasks.length} tâche(s) trouvée(s)
            </p>
            {parsedTasks.length > 0 && (
              <Button size="sm" variant="outline" onClick={handleCreateAll} className="text-xs">
                <Plus className="w-3 h-3 mr-1" /> Tout ajouter
              </Button>
            )}
          </div>

          {parsedTasks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Aucune tâche actionnable trouvée dans ces notes.
            </p>
          ) : (
            <div className="space-y-2">
              {parsedTasks.map((task, i) => (
                <div key={i} className={`p-3 rounded-lg border transition-colors ${
                  created[i]
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-white dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                }`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.task}</p>
                      <div className="flex gap-2 mt-1 flex-wrap items-center">
                        {task.priority && (
                          <span className="text-xs px-1.5 py-0.5 rounded text-white font-medium"
                            style={{ backgroundColor: PRIORITY_COLORS[task.priority] || '#5559DF' }}>
                            {task.priority}
                          </span>
                        )}
                        {task.assignee && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">👤 {task.assignee}</span>
                        )}
                        {task.due_date && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">📅 {task.due_date}</span>
                        )}
                      </div>
                    </div>
                    {created[i] ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCreateOne(task, i)}
                        disabled={creating[i]}
                        className="h-7 px-2 flex-shrink-0"
                      >
                        {creating[i]
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <ChevronRight className="w-3 h-3" />}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main AIPanel ────────────────────────────────────────────────────────────
const AIPanel = ({ board, workspaceId, onClose, onTaskCreated }) => {
  const [activeTab, setActiveTab] = useState('create');

  const handleTaskCreated = () => {
    onTaskCreated?.();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b dark:border-gray-700">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1C3E53] to-[#4A90A4] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Assistant IA</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{board?.name}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b dark:border-gray-700">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-[#1C3E53] dark:text-[#4A90A4] border-b-2 border-[#1C3E53] dark:border-[#4A90A4]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:block">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {activeTab === 'create' && (
          <CreateTaskTab
            board={board}
            workspaceId={workspaceId}
            onTaskCreated={handleTaskCreated}
          />
        )}
        {activeTab === 'analyze' && (
          <AnalyzeTab board={board} />
        )}
        {activeTab === 'notes' && (
          <ParseNotesTab
            board={board}
            workspaceId={workspaceId}
            onTaskCreated={handleTaskCreated}
          />
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
          Propulsé par l'IA · Les suggestions peuvent être inexactes
        </p>
      </div>
    </div>
  );
};

export default AIPanel;
